import { useCallback, useRef } from 'react';
import { callBackend } from '../../../utils/api';
import { buildCanonicalPosSizingPortfolioRows } from '../../../utils/posSizingPortfolioContract';
import {
    buildLightweightFactorContext,
} from '../dashboardUtils';
import {
    isValidPosSizingPublicationBase,
    POS_SIZING_ACTUAL_SYNC_MODE,
    POS_SIZING_ATOMIC_PUBLICATION_CONTRACT,
    POS_SIZING_WATCHLIST_START_MODE,
    requireAtomicPublicationReceipt,
    requirePosSizingPublicationCapability,
} from '../../../utils/posSizingPublicationContract';

function formatIncompleteMarketDataWarning(identifiers, fallbackCount = 0) {
    const displayIdentifiers = Array.from(new Set(
        (Array.isArray(identifiers) ? identifiers : [])
            .map((value) => String(value ?? '').trim())
            .filter(Boolean),
    ));
    const count = Math.max(displayIdentifiers.length, fallbackCount);
    if (!count) {
        return null;
    }
    const shown = displayIdentifiers.slice(0, 5);
    const remainder = Math.max(0, count - shown.length);
    const subject = shown.length
        ? `${shown.join(', ')}${remainder ? ` and ${remainder} more` : ''}`
        : `${count} securit${count === 1 ? 'y' : 'ies'}`;
    return `Position Sizing synced available securities. ${subject} ${count === 1 ? 'was' : 'were'} excluded because required Position Sizing data is incomplete.`;
}

export default function usePosSizingPersistence({
    onPosSizingSaved,
    maxPosSize,
    normalVol,
    selectedFactorModel,
    selectedBetaSource,
    posSizingIdioVolSource,
    factorSnapshotDate,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen,
    persistPosSizingPortfolioRef,
    factorDataModel,
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
    setError,
    setPosSizingData,
    setPosSizingLoading,
    setPosSizingLoaded,
    posSizingRequestIdRef,
    posSizingPublicationBaseRef,
    resolveActualSyncStartingValues,
}) {
    const contextRequestRef = useRef(null);
    const persistPosSizingPortfolio = useCallback(async (rows = []) => {
        const sourceRows = Array.isArray(rows) ? rows : [];
        let payloadRows;
        try {
            payloadRows = buildCanonicalPosSizingPortfolioRows(sourceRows);
        } catch (contractError) {
            console.error(
                'Refusing to persist invalid canonical pos-sizing rows:',
                contractError,
            );
            setSnackbarMessage('Failed to sync saved pos-sizing portfolio identity.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return false;
        }
        const publicationBase = posSizingPublicationBaseRef.current;
        if (!isValidPosSizingPublicationBase(publicationBase)) {
            setSnackbarMessage('Position Sizing changed. Refresh before saving.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return false;
        }
        const requestPayloadBase = {
            contract: POS_SIZING_ATOMIC_PUBLICATION_CONTRACT,
            publication_base: publicationBase,
            rows: payloadRows,
            max_pos_size: maxPosSize,
            normal_vol: normalVol,
        };

        try {
            const response = await callBackend(
                'set_whatif_portfolio_v1',
                'POST',
                requestPayloadBase,
            );
            if (!isValidPosSizingPublicationBase(response?.publication_base)) {
                throw new Error('Position sizing persistence did not return a current publication base.');
            }
            posSizingPublicationBaseRef.current = response.publication_base;
            await onPosSizingSaved();
            return true;
        } catch (error) {
            posSizingPublicationBaseRef.current = null;
            console.error('Error syncing pos-sizing portfolio:', error);
            setSnackbarMessage('Failed to sync saved pos-sizing portfolio.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return false;
        }
    }, [
        maxPosSize,
        normalVol,
        onPosSizingSaved,
        posSizingPublicationBaseRef,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
    ]);
    persistPosSizingPortfolioRef.current = persistPosSizingPortfolio;

    // Sync Draft, a first draft from the Watchlist and a file upload each
    // publish a fresh context, so they name the same factor selection,
    // default targets and settings. `startingValues` overrides this render's
    // default targets with values resolved after it.
    const buildFreshContextPublicationFields = useCallback(({
        matricesOverride = null,
        idioVolSource = posSizingIdioVolSource,
        maxPosSize: requestedMaxPosSize,
        normalVol: requestedNormalVol,
        startingValues = null,
    } = {}) => {
        const factorContext = buildLightweightFactorContext({
            factorModel: selectedFactorModel,
            factorDataModel,
            factorSnapshotDate,
            betaSource: selectedBetaSource,
            idioVolSource,
            override: matricesOverride,
        });
        return {
            factorModel: factorContext.factorModel,
            factor_data_model: factorContext.factorDataModel,
            factor_snapshot_date: factorContext.factorSnapshotDate,
            beta_source: factorContext.betaSource,
            idio_vol_source: factorContext.idioVolSource,
            default_target_vol_pct: startingValues
                ? startingValues.target_vol_pct
                : defaultTargetVolPct,
            default_target_vol_dollar: startingValues
                ? startingValues.target_vol_dollar
                : defaultTargetVolDollar,
            default_scaler: startingValues ? startingValues.scaler : defaultScaler,
            max_pos_size: requestedMaxPosSize ?? maxPosSize,
            normal_vol: requestedNormalVol ?? normalVol,
        };
    }, [
        defaultScaler,
        defaultTargetVolDollar,
        defaultTargetVolPct,
        factorDataModel,
        factorSnapshotDate,
        maxPosSize,
        normalVol,
        posSizingIdioVolSource,
        selectedBetaSource,
        selectedFactorModel,
    ]);

    // Creates the first draft from Watchlist rows while Actual is empty. The
    // caller reloads the workspace afterwards, as it does after Sync Draft.
    const startPosSizingFromWatchlist = useCallback(async ({
        rows,
        maxPosSize: startMaxPosSize,
        normalVol: startNormalVol,
    }) => {
        const publicationBase = posSizingPublicationBaseRef.current;
        if (
            !isValidPosSizingPublicationBase(publicationBase)
            || publicationBase.portfolio_id !== null
        ) {
            throw new Error('Position Sizing changed. Refresh before saving.');
        }
        // An older workspace read must not overwrite this publication's base.
        posSizingRequestIdRef.current += 1;
        const publicationFields = buildFreshContextPublicationFields({
            maxPosSize: startMaxPosSize,
            normalVol: startNormalVol,
        });
        try {
            await requirePosSizingPublicationCapability(
                callBackend,
                POS_SIZING_WATCHLIST_START_MODE,
            );
            const response = await callBackend('publish_pos_sizing_v1', 'POST', {
                contract: POS_SIZING_ATOMIC_PUBLICATION_CONTRACT,
                mode: POS_SIZING_WATCHLIST_START_MODE,
                publication_base: publicationBase,
                rows,
                ...publicationFields,
            });
            const results = response?.results;
            const receipt = requireAtomicPublicationReceipt(
                response,
                buildCanonicalPosSizingPortfolioRows(results?.pos_sizing),
                POS_SIZING_WATCHLIST_START_MODE,
            );
            posSizingPublicationBaseRef.current = receipt.publication_base;
            setError(null);
            setPosSizingData(results?.pos_sizing || []);
            await onPosSizingSaved({ replacesWorkspace: true });
            const excludedCount = Array.isArray(receipt?.excluded_cognive_ids)
                ? receipt.excluded_cognive_ids.length
                : 0;
            if (excludedCount > 0) {
                setSnackbarMessage(formatIncompleteMarketDataWarning(
                    receipt?.excluded_identifiers,
                    excludedCount,
                ));
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
            }
            return { results, receipt };
        } catch (error) {
            posSizingPublicationBaseRef.current = null;
            throw error;
        }
    }, [
        buildFreshContextPublicationFields,
        onPosSizingSaved,
        posSizingPublicationBaseRef,
        posSizingRequestIdRef,
        setError,
        setPosSizingData,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
    ]);

    const handlePosSizingUploadSuccess = useCallback(async (updatedResults, receipt) => {
        posSizingRequestIdRef.current += 1;
        posSizingPublicationBaseRef.current = receipt.publication_base;
        setPosSizingData(updatedResults.pos_sizing || []);
        setPosSizingLoaded(true);
        await onPosSizingSaved({ replacesWorkspace: true });
    }, [
        onPosSizingSaved,
        posSizingRequestIdRef,
        posSizingPublicationBaseRef,
        setPosSizingData,
        setPosSizingLoaded,
    ]);

    // The coordinator owns acceptance and serialization. The server selects
    // refresh versus Actual handoff from its durable parent/import evidence.
    const reconcilePosSizingContext = useCallback(async ({
        acceptance,
        contextUpdate,
        matricesOverride = null,
        idioVolSource = posSizingIdioVolSource,
        requestId,
        requireRebuild = false,
        isCurrentRequest = () => true,
        stagePublicationState = null,
        preparedTarget = null,
    } = {}) => {
        const activeRequestId = ++posSizingRequestIdRef.current;
        const isCurrent = () => posSizingRequestIdRef.current === activeRequestId && isCurrentRequest();
        let starting = null;
        const retryPayload = requestId && contextRequestRef.current?.requestId === requestId
            ? contextRequestRef.current.payload : null;
        if (!retryPayload && (contextUpdate?.mode === 'actual_sync'
            || ![defaultTargetVolPct, defaultTargetVolDollar, defaultScaler].every(Number.isFinite))) {
            starting = preparedTarget?.actualStartingValues ?? await resolveActualSyncStartingValues();
        }
        if (!isCurrent()) return null;
        const publicationFields = buildFreshContextPublicationFields({
            matricesOverride,
            idioVolSource,
            ...(starting ? { startingValues: starting.values } : {}),
            ...(starting?.firstDraft ? {
                maxPosSize: starting.values.max_pos_size,
                normalVol: starting.values.normal_vol,
            } : {}),
        });
        const publicationBase = contextUpdate?.publication_base
            ?? posSizingPublicationBaseRef.current;
        if (!isValidPosSizingPublicationBase(publicationBase)) {
            throw new Error('Position Sizing changed. Reload its context before updating.');
        }
        const payload = retryPayload ?? {
            contract: POS_SIZING_ATOMIC_PUBLICATION_CONTRACT,
            ...publicationFields,
            acceptance,
            publication_base: publicationBase,
            request_id: requestId,
            require_rebuild: requireRebuild,
        };
        contextRequestRef.current = { requestId, payload };
        const response = await callBackend('reconcile_pos_sizing_context_v1', 'POST', payload);
        if (!isCurrent()) return null;
        // Hydration may legitimately advance the shared request ID next. The
        // accepted receipt stays fenced by its exact base and outer owner.
        const publishAccepted = (apply, base) => {
            if (typeof stagePublicationState !== 'function') { apply(); return; }
            stagePublicationState(apply, () => {
                const currentBase = posSizingPublicationBaseRef.current;
                return isCurrentRequest() && isValidPosSizingPublicationBase(currentBase)
                    && currentBase.portfolio_id === base.portfolio_id
                    && currentBase.portfolio_row_version === base.portfolio_row_version
                    && currentBase.watchlist_revision_id === base.watchlist_revision_id;
            });
        };
        if (response?.unchanged === true && response?.success === true) return response;
        if (response?.recovered === true && response?.receipt?.committed === true
            && response.receipt.contract === POS_SIZING_ATOMIC_PUBLICATION_CONTRACT
            && isValidPosSizingPublicationBase(response.receipt.publication_base)) {
            posSizingPublicationBaseRef.current = response.receipt.publication_base;
            await onPosSizingSaved({ skipStatusRefresh: true,
                ...(typeof stagePublicationState === 'function' ? { stagePublicationState, preparedTarget,
                    publicationBase: response.receipt.publication_base } : {}) });
            return response;
        }
        const rows = buildCanonicalPosSizingPortfolioRows(response?.results?.pos_sizing);
        const receipt = requireAtomicPublicationReceipt(response, rows, response?.receipt?.publication_kind);
        if (!['actual_sync', 'context_refresh'].includes(receipt.publication_kind)) {
            throw new Error('Unexpected Position Sizing context publication.');
        }
        posSizingPublicationBaseRef.current = receipt.publication_base;
        const excludedCount = Array.isArray(receipt.excluded_cognive_ids) ? receipt.excluded_cognive_ids.length : 0;
        const warning = formatIncompleteMarketDataWarning(receipt.excluded_identifiers, excludedCount);
        publishAccepted(() => {
            setError(null);
            if (warning) {
                setSnackbarMessage(warning);
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
            }
        }, receipt.publication_base);
        // Hydration publishes the table and attributes together. Keep the last
        // valid workspace until that matching result is available.
        await onPosSizingSaved({ skipStatusRefresh: true,
            ...(typeof stagePublicationState === 'function' ? { stagePublicationState, preparedTarget,
                publicationBase: receipt.publication_base } : {}) });
        return response;
    }, [
        buildFreshContextPublicationFields, onPosSizingSaved, posSizingIdioVolSource,
        posSizingPublicationBaseRef, posSizingRequestIdRef, resolveActualSyncStartingValues,
        defaultTargetVolPct, defaultTargetVolDollar, defaultScaler,
        setError, setSnackbarMessage, setSnackbarOpen, setSnackbarSeverity,
    ]);

    return {
        buildFreshContextPublicationFields,
        startPosSizingFromWatchlist,
        handlePosSizingUploadSuccess,
        reconcilePosSizingContext,
    };
}
